import type { LooseObject } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3 } from '../..';
import { useStack } from '../../hooks/useStack';
import { CommandPageContent } from './CommandPageContent';

export type ImageTooltip = {
  kind: 'image';
  src: string;
  description: string;
};

export type ContentTooltip = {
  kind: 'content';
  content: React.ReactNode;
};

export type CommandTooltip = ImageTooltip | ContentTooltip;

export type CommandActionInfo<A> = {
  id: string;
  title: (context: CommandMenuContext<A>) => React.ReactNode;
  rawTitle: string;
  category?: string;
};

export type GotoPageAction<A> = CommandActionInfo<A> & {
  kind: 'goto';
  id: PageId;
  args?: LooseObject;
};

export type VoidAction<A> = CommandActionInfo<A> & {
  kind: 'void';
  action: (props: A) => void;
};

export type Action<A> = GotoPageAction<A> | VoidAction<A>;

export type FreePage<A> = {
  kind: 'free';
  id: PageId;
  title: React.ReactNode;
  rawTitle: string;
  body: (context: CommandMenuContext<A>, args?: LooseObject) => React.ReactNode;
};

export type ActionsPage<A> = {
  kind: 'actions';
  id: PageId;
  title: React.ReactNode;
  rawTitle: string;
  actions: Action<A>[];
  searchable: boolean;
};

export type TabbedPage<A> = {
  kind: 'tabbed';
  id: PageId;
  tabs: {
    label: string;
    pageId: PageId;
    icon?: (context: CommandMenuContext<A>) => React.ReactNode;
  }[];
};

type PageId = 'ROOT' | string;

export type PageIdWithArgs = {
  id: PageId;
  args?: LooseObject;
};

export type Page<A> = FreePage<A> | ActionsPage<A> | TabbedPage<A>;

export interface ActionItemProps {
  ref: React.Ref<HTMLLIElement>;
  onClick: () => void;
  onFocus: () => void;
  onMouseMove: () => void;
  onPointerLeave: () => void;
}

export type CommandMenuInput = { kind: 'visible' } | { kind: 'external'; filterText: string };

export type CommandMenuContext<A> = {
  ctx: A;
  currentPagesLength: number;
  getPage: (id: PageId) => Page<A> | null;
  pushPage: (id: PageId, args?: LooseObject) => void;
  popPage: () => void;
  onExit: () => void;
  input: CommandMenuInput;
};

export interface CommandMenuProps<A> {
  getPages: (ctx: A) => Page<A>[];
  ctx: A;
  rootPageId?: PageIdWithArgs;
  onOpenChange: (value: boolean) => void;
  input?: CommandMenuInput;
}

export const CommandMenu = <A,>(props: CommandMenuProps<A>) => {
  const { getPages, ctx, rootPageId, onOpenChange } = props;
  const input = props.input ?? { kind: 'visible' };

  const [_pageIds, pageIdActions] = useStack<PageIdWithArgs>([rootPageId ?? { id: 'ROOT' }]);
  const onExit = React.useCallback(() => {
    onOpenChange(false);
    pageIdActions.reset();
  }, [pageIdActions, onOpenChange]);

  const pages = React.useMemo(() => getPages(ctx), [getPages, ctx]);

  const { currentPage, args } = React.useMemo(() => {
    const pageIdWithArgs = pageIdActions.peek();

    const page = pages.find((p) => p.id === pageIdWithArgs?.id);

    if (page === undefined) {
      throw new Error(`Page ${pageIdWithArgs?.id} not found`);
    }

    return { currentPage: page, args: pageIdWithArgs?.args };
  }, [pages, pageIdActions]);

  const commandMenuContext: CommandMenuContext<A> = {
    ctx,
    currentPagesLength: pageIdActions.length,
    onExit,
    getPage: (id: PageId) => pages.find((p) => p.id === id) ?? null,
    pushPage: (id: PageId, args?: LooseObject) => {
      pageIdActions.push({ id, args });
    },
    popPage: () => {
      pageIdActions.pop();
    },
    input,
  };

  return (
    <ChakraV3.Box rounded='xl' borderRadius='md' bg='white' shadow='2xl'>
      <CommandPageContent
        key={currentPage.id}
        page={currentPage}
        context={commandMenuContext}
        args={args}
      />
    </ChakraV3.Box>
  );
};
